import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import FormLabel from '@mui/material/FormLabel'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { XPKinshipRelationshipTypes } from 'utils/constants/DueDiligenceRelations'
import { hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'

export default function QuestionNine(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialData = {
    relative_name: '',
    occupation: '',
    kinship_degree: 'default',
  }

  const initialErrors = {
    relative_name: false,
    occupation: false,
    kinship_degree: false,
  }

  const initializeFormState = (data) => {
    return {
      hasKinshipRelations: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = event.target.value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasKinshipRelations = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const handleSelectChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.kinship_degree = event.target.value
    setActivityData(newActivityData)
  }

  const hasKinshipRelations = () => {
    return !(formState.items.length === 0)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (formState.items.length > 0 && formState.hasKinshipRelations === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (
      !skipValidation &&
      formState.hasKinshipRelations &&
      !hasKinshipRelations()
    ) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'kinship_xp',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (key === 'kinship_degree' && activityData[key] === 'default') {
        newActivityErrors[key] = true
      }
      if (activityData[key] === '') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveActivity = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const buildActivity = (activityInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Nome completo do colaborador XP: </span>
          {activityInfo.relative_name}
        </p>
        <p>
          <span>Cargo: </span>
          {activityInfo.occupation}
        </p>

        <p>
          <span>Grau de parentesco: </span>
          {XPKinshipRelationshipTypes[activityInfo.kinship_degree]}
        </p>
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasKinshipRelations !== questionData.items.length > 0
    setEdited(hasChanged)
    if (formState.items.length > 0 && formState.hasKinshipRelations === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 9</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Você tem parentesco com algum funcionário e/ou estagiário da XP
              Investimentos?{' '}
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasKinshipRelations ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasKinshipRelations ? (
            <div className={classes.formFields}>
              <TextField
                id="relative_name"
                label="Nome completo do(a) colaborador(a) XP"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.relative_name}
                error={activityErrors.relative_name}
                size="small"
              />

              <TextField
                id="occupation"
                label="Cargo"
                onChange={handleFieldChange}
                required
                value={activityData.occupation}
                error={activityErrors.occupation}
                fullWidth
                size="small"
              />

              <Select
                onChange={handleSelectChange}
                value={activityData.kinship_degree}
                error={activityErrors.kinship_degree}
                size="small"
              >
                <MenuItem value="default" disabled>
                  Selecione o grau de parentesco
                </MenuItem>
                {Object.keys(XPKinshipRelationshipTypes).map((key) => (
                  <MenuItem key={key} value={key}>
                    {XPKinshipRelationshipTypes[key]}
                  </MenuItem>
                ))}
              </Select>

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveActivity}
                mustRemove={formState.hasKinshipRelations === false}
              >
                {buildActivity(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasKinshipRelations && formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
